import React, { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Search, Bookmark,
  Settings as SettingsIcon, Wallet, LogOut, Menu, X, Building2, LayoutDashboard,
  Info, ArrowUpRight,
} from "lucide-react";

const APP_VERSION = "v1.0";
import { TID } from "@/lib/testIds";
import UsageBadge from "@/components/UsageBadge";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import BrandSwitcher from "@/components/BrandSwitcher";
import WorkspaceBanner from "@/components/WorkspaceBanner";
import BrandGoneBanner from "@/components/BrandGoneBanner";
import BrandUrlSync from "@/components/BrandUrlSync";

// Scan-hub model: ONE scan destination (Find Customers). Pain Points / Market /
// Partners are LENSES (tabs) on a scan's results, not separate sidebar tools.
const navItems = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-link-dashboard", primary: true },
  {
    to: "/app/find-customers", label: "Find Customers", icon: Search, testId: TID.sidebarLinkFindCustomers,
    children: [
      { to: "/app/find-customers?lens=find_customers", lens: "find_customers", label: "Customers", emoji: "🔥" },
      { to: "/app/find-customers?lens=market", lens: "market", label: "Market Insights", emoji: "📊", comingSoon: true },
      { to: "/app/find-customers?lens=partners", lens: "partners", label: "Find Partners", emoji: "🤝", comingSoon: true },
    ],
  },
  { to: "/app/saved-leads", label: "Saved Leads", icon: Bookmark, testId: TID.sidebarLinkSavedLeads },
  { to: "/app/brand", label: "Brand Foundation", icon: Building2, testId: TID.sidebarLinkBrand },
];

const bottomItems = [
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testId: TID.sidebarLinkSettings },
  { to: "/app/billing", label: "Billing", icon: Wallet, testId: TID.sidebarLinkBilling },
];

function VersionBadge() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/15 px-2 py-0.5 text-[10px] font-mono text-white/70 hover:text-white transition-colors"
        aria-label="Version info"
      >
        {APP_VERSION}
        <Info className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-60 bg-lm-surface text-lm-text border border-lm-border rounded-xl shadow-2xl p-3.5 z-50 animate-[fadeIn_0.15s_ease]">
          <div className="text-xs font-mono uppercase tracking-widest text-lm-primary mb-1">Leadmato {APP_VERSION}</div>
          <p className="text-xs text-lm-text2 leading-relaxed">
            You're on the very first release of your one-scan, five-lenses customer finder — the best is already on the way. 🍅
          </p>
          <a
            href="/roadmap"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-lm-primary hover:underline mt-2.5"
          >
            Learn more <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
    </div>
  );
}

function NavItem({ item, onClick }) {
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={item.testId}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isActive
            ? "bg-white/15 text-white font-medium"
            : "text-white hover:bg-white/[0.08]"
        }`
      }
    >
      <item.icon className="w-4 h-4" strokeWidth={1.8} />
      <span className="flex-1">{item.label}</span>
      {item.comingSoon && (
        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-lm-brand-green/15 text-lm-brand-green">
          Soon
        </span>
      )}
    </NavLink>
  );
}

export default function AppShell() {
  const { user, workspace, logout, workspaceStatus, routeChanged } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close user menu on outside click or Escape — always-on, capture-phase (same
  // robust pattern as the workspace switcher).
  useEffect(() => {
    const onDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setUserMenuOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  const navigate = useNavigate();
  const location = useLocation();

  // Self-heal §3.4 — a route change inside /app is a free recovery trigger.
  // AuthProvider is mounted OUTSIDE BrowserRouter and cannot use useLocation,
  // so the shell forwards it. AuthContext throttles this to once per 5s.
  useEffect(() => {
    routeChanged?.();
  }, [location.pathname, routeChanged]);

  // Active lens (for sidebar sub-menu highlight): from ?lens=, or default
  // "find_customers" when on the Find Customers path.
  const currentLens =
    new URLSearchParams(location.search).get("lens") ||
    (location.pathname === "/app/find-customers" ? "find_customers" : null);

  const renderNav = (onClick) =>
    navItems.map((i) => (
      <div key={i.to}>
        <NavItem item={i} onClick={onClick} />
        {i.children && (
          <div className="ml-3.5 mt-1 mb-1 flex flex-col gap-0.5 border-l border-white/10 pl-3">
            {i.children.map((c) => {
              const active = currentLens === c.lens;
              // Coming-soon lenses are shown but not clickable.
              if (c.comingSoon) {
                return (
                  <div
                    key={c.to}
                    title={`${c.label} — coming soon`}
                    className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] text-white/40 cursor-not-allowed"
                  >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-white/20" />
                    {c.label}
                    <span className="ml-auto rounded-full bg-white/10 px-1.5 py-0.5 text-[8px] font-mono uppercase tracking-wide">Soon</span>
                  </div>
                );
              }
              return (
                <Link
                  key={c.to}
                  to={c.to}
                  onClick={onClick}
                  className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-colors ${
                    active ? "bg-white/15 text-white font-medium" : "text-white/70 hover:bg-white/[0.08] hover:text-white"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${active ? "bg-white" : "bg-white/40"}`} />
                  {c.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    ));

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-lm-bg flex">
      {/* Renders nothing. Keeps this TAB's ?brand= and the seated brand in
          agreement — mounted once here because it needs the router, and one
          instance is the only way the two directions of sync stay a single
          arbiter rather than several disagreeing ones. */}
      <BrandUrlSync />
      {/* Sidebar (desktop) - dark, matches homepage navbar */}
      <aside
        data-testid={TID.sidebar}
        className="hidden md:flex md:flex-col w-60 border-r border-[color:var(--lm-dark-border)] bg-[color:var(--lm-dark)] text-white fixed top-0 left-0 bottom-0 z-20"
      >
        <div
          className="h-16 px-5 flex items-center gap-2.5 border-b border-[color:var(--lm-dark-border)]"
          data-testid={TID.sidebarBrand}
        >
          <Link to="/" className="flex items-center" aria-label="Leadmato home">
            <img src="/leadmato-logo.png" alt="Leadmato" className="h-6 w-auto select-none" draggable="false" />
          </Link>
          <VersionBadge />
        </div>
        <nav className="flex-1 p-3 flex flex-col gap-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-widest text-white/40 px-3 pt-3 pb-2 font-mono">Discover</div>
          {renderNav()}
        </nav>
        <div className="p-3 border-t border-[color:var(--lm-dark-border)] flex flex-col gap-1">
          {bottomItems.map((i) => <NavItem key={i.to} item={i} />)}
        </div>
      </aside>

      {/* Mobile drawer - dark, matches desktop sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-[color:var(--lm-dark)] text-white border-r border-[color:var(--lm-dark-border)] flex flex-col z-50">
            <div className="h-16 px-5 flex items-center justify-between border-b border-[color:var(--lm-dark-border)]">
              <div className="flex items-center" aria-label="Leadmato home">
                <img src="/leadmato-logo.png" alt="Leadmato" className="h-6 w-auto select-none" draggable="false" />
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="text-white/70 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-3 flex flex-col gap-1 overflow-y-auto">
              {renderNav(() => setMobileOpen(false))}
              <div className="pt-2 mt-2 border-t border-[color:var(--lm-dark-border)]" />
              {bottomItems.map((i) => <NavItem key={i.to} item={i} onClick={() => setMobileOpen(false)} />)}
            </nav>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 md:ml-60 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 border-b border-lm-border bg-lm-surface/85 backdrop-blur-md sticky top-0 z-10 flex items-center px-4 md:px-8 gap-4">
          <button
            data-testid={TID.topbarMobileMenu}
            className="md:hidden -ml-1 p-2 rounded-md hover:bg-lm-tint"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          {/* A ROW: workspace then brand, side by side. Was a block, which
              stacked them and doubled the header's height. */}
          <div
            className="flex-1 min-w-0 flex items-center gap-2 md:gap-3"
            data-testid={TID.topbarWorkspaceName}
          >
            {/* §4: the top bar says NOTHING about the error. It goes
                neutral-blank and points at the one banner that owns the
                message. It never says "Loading workspace…" past the honesty
                line — that was the lie this whole spec exists to kill. */}
            <WorkspaceSwitcher
              current={{ name: workspace?.name, role: workspace?.role }}
              status={workspaceStatus}
            />
            {/* Its own control, beside the workspace one rather than buried in
                its menu. Brand is what you work IN and changes constantly;
                workspace changes rarely. BrandSwitcher records the argument
                this overturned, which was not a bad one. */}
            <BrandSwitcher />
          </div>
          <div
            data-testid={TID.topbarUsageBadge}
          >
            <UsageBadge
              usage={workspace?.usage_today}
              entitlements={workspace?.entitlements}
              status={workspaceStatus}
              testId={TID.topbarUsageBadge + "-inner"}
            />
          </div>
          <div className="relative" ref={userMenuRef}>
            <button
              data-testid={TID.topbarUserMenu}
              className="relative w-9 h-9 rounded-full bg-lm-primary text-white font-display font-semibold flex items-center justify-center overflow-hidden"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-label="User menu"
            >
              {(user?.name || "?").charAt(0).toUpperCase()}
              <img
                src={user?.picture || `https://api.dicebear.com/9.x/shapes/svg?seed=${encodeURIComponent(user?.email || user?.user_id || user?.name || "user")}&backgroundType=gradientLinear`}
                alt={user?.name || "User"}
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-lm-surface border border-lm-border rounded-lg shadow-lg py-1 z-30">
                <div className="px-4 py-2 border-b border-lm-border">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-lm-text3 truncate">{user?.email}</div>
                </div>
                <button
                  data-testid={TID.topbarLogout}
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-lm-text hover:bg-lm-tint inline-flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* The ONE owner of the "something's wrong" message (§4). Sticky
            directly under the top bar, above page content, full width. */}
        <WorkspaceBanner />
        {/* A finished event, not a live fault — see BrandGoneBanner. */}
        <BrandGoneBanner />

        <main className="flex-1 p-6 md:p-8 lg:p-10 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
